import React, { useCallback, useState } from 'react';
import { Client as AuthClient } from '@apps-common/auth';
import { AuthProvider } from '@apps-common/auth-react';
import { State } from '@apps-common/auth/client';
import { connect } from 'react-redux';

import AuthenticationContextProvider from '@modules/auth/components/context';
import { setLoggedIn } from '@src/actions/auth';
import { setAccount } from '@src/actions/accounts';
import { setProject } from '@src/actions/project';
import config from '@config/config';
import { setupOnAuthSuccess, getClaims } from '@modules/auth/components/lib/auth/setup';

import { setAuthState } from './components/lib';
import { AuthorizationProps } from './types';
import FullPageLoader from './components/FullPageLoader';

const authClient = new AuthClient({
  clientId: config.auth0Config?.clientId || config.clientId,
  redirectUri: `${window.location.origin}${window.location.pathname}`,
  postLogoutRedirectUri: `${window.location.origin}${window.location.pathname}`,
  issuer: config.auth0Config?.issuer || `${config.keycloakAuthUrl}/realms/${config.keycloakRealm}`,
});

const withAuthorization = (WrappedComponent: React.ComponentClass<AuthorizationProps>) => {
  function Authorized(props: AuthorizationProps) {
    const [authInProgress, setAuthInProgress] = useState<boolean>(true);

    const onAuthChange = useCallback(async (newState: State) => {
      try {
        if (newState.pending) {
          return;
        }
        if (newState.error) {
          throw newState.error;
        }
        if (!newState.user) {
          throw Error('Claims are missing.');
        }
        setAuthState(newState);
        setAuthInProgress(true);
        await setupOnAuthSuccess(getClaims(newState.user), props);
      } catch (err) {
        console.error(err);
      } finally {
        setAuthInProgress(false);
      }
    }, []);

    return (
      <AuthProvider client={authClient} onChange={onAuthChange} private={!authInProgress}>
        {authInProgress ? (
          <FullPageLoader />
        ) : (
          <AuthenticationContextProvider>
            <WrappedComponent {...props} />
          </AuthenticationContextProvider>
        )}
      </AuthProvider>
    );
  }
  return connect(null, mapDispatchToProps)(Authorized);
};

const mapDispatchToProps = {
  setLoggedIn,
  setAccount,
  setProject,
};

export default withAuthorization;
